import { i18n, type Messages } from "@lingui/core";

import { messages as enMessages } from "./locales/en/messages";
import { messages as esMessages } from "./locales/es/messages";
import { messages as frMessages } from "./locales/fr/messages";
import { messages as deMessages } from "./locales/de/messages";
import { messages as ptMessages } from "./locales/pt/messages";
import { messages as zhMessages } from "./locales/zh/messages";
import { messages as plMessages } from "./locales/pl/messages";
import { messages as jaMessages } from "./locales/ja/messages";
import { messages as elMessages } from "./locales/el/messages";

import {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  coerceToSupported,
  type SupportedLanguage,
} from "~/utils/i18n";

// One Lingui singleton shared across requests. All six catalogs are loaded up
// front so a client-side `activateLocale` (e.g. the signup language picker)
// re-renders instantly without an async catalog fetch. This is safe while each
// render activates a locale synchronously with no async work crossing the
// locale boundary; if async loaders start resolving mid-render, switch to a
// per-request `setupI18n()` instance (Lingui SSR docs).
const CATALOGS: Record<SupportedLanguage, Messages> = {
  en: enMessages,
  fr: frMessages,
  es: esMessages,
  de: deMessages,
  pt: ptMessages,
  zh: zhMessages,
  pl: plMessages,
  ja: jaMessages,
  el: elMessages,
};

let loaded = false;

function loadAllCatalogs() {
  for (const locale of SUPPORTED_LANGUAGES) {
    i18n.load(locale, CATALOGS[locale]);
  }
  loaded = true;
}

export function activateLocale(
  value: string | null | undefined,
): SupportedLanguage {
  if (!loaded) loadAllCatalogs();
  const locale = coerceToSupported(value);
  i18n.activate(locale);
  return locale;
}

if (!loaded) {
  loadAllCatalogs();
  i18n.activate(DEFAULT_LANGUAGE);
}

export { i18n };
